import Service, { inject as service } from '@ember/service';
import { all, dropTask, timeout, waitForProperty } from 'ember-concurrency';
import { computed, set, setProperties } from '@ember/object';
import { showModal } from 'sh-www/utils/modal-controls';
import { getMsFromMinutes } from 'sh-www/utils/date-and-time';
import { STORAGE_KEYS } from 'sh-www/services/local-storage-fallback';
import { isForbiddenError, isUnauthorizedError, isServiceUnavailableError } from 'sh-www/utils/adapter-errors';
import Config from 'sh-www/config/environment';

const getTOSText = ({ isBuyer }) => {
  const bodyText = `Our Terms of Service have changed since you last logged in. Please <a target="_blank" class="lsg-black black-underline" href="/legal/terms-of-service/">review them</a> and accept them to continue.`;

  return isBuyer
    ? `${bodyText}<br/><br/>Read the Master Brand Agreement <a class="lsg-black black-underline" target="_blank" href="/legal/master-publisher-agreement/">here</a>`
    : bodyText;
};

class HeartbeatStateManagerHybrid extends Service {
  @service('commands.company-commands')
  companyCommandService;

  @service('state-manager-hybrids/activity-feed-state-manager-hybrid')
  activityFeedStateHybrid;

  @service('commands/user-commands')
  userCommandService;

  @service
  api;

  @service('local-storage-fallback')
  storage;

  @service
  session;

  @service
  store;

  @service
  router;

  /////////////////////////////////
  //  The Actual Heartbeat State Properties
  /////////////////////////////////

  // Global

  pollingInterval = Config.DEFAULT_HEARTBEAT_POLLING_INTERVAL;
  version = undefined;

  // ActivityFeed
  /**
   * Hash of the state of the activity feed
   *
   * @type {string}
   */
  activityFeedHash = undefined;

  /**
   * The current payout account status of the user
   *
   * Number     - when we have a payout account with a specific state
   * undefined  - when this is our initial state, and a value has NOT been set yet.
   * null       - we've initialized the state, but the user has not signed up for Payoneer yet.
   *
   * @type {Number|null|undefined}
   */
  payoutAccountStatus = undefined;

  /**
   * Whether the user's payout account requires attention (upcoming payments + incomplete setup).
   *
   * @type {Boolean|undefined}
   */
  payoutAccountRequiresAttention = undefined;

  /** @type {String} */
  hyperwalletStatus = undefined;

  // Messages

  threadsUnreadInbox = 0;
  threadsUnreadArchive = 0;
  mostRecentInboxThreads = [];

  inboxHash = undefined;
  archiveHash = undefined;

  // Web

  minWebVersion = undefined;

  // Admin data
  adminBag = undefined;

  /////////////////////////////////
  /////////////////////////////////

  /**
   * @type {boolean}
   */
  needsLegal = false;

  /**
   * @type {boolean}
   */
  heartbeatPaused = false;

  @computed('session.currentUser')
  get currentUser() {
    return this.session.currentUser;
  }

  /**
   * Value used to detect a change in either of these properties
   */
  @computed('accountLocked', 'daysUntilDelinquentOrInfinity')
  get delinquencyValues() {
    return {
      accountLocked: this.accountLocked,
      daysUntilDelinquentOrInfinity: this.daysUntilDelinquentOrInfinity,
    };
  }

  @computed('daysUntilDelinquent')
  get daysUntilDelinquentOrInfinity() {
    const daysUntilDelinquent = Number.parseInt(this.daysUntilDelinquent, 10);

    return Number.isInteger(daysUntilDelinquent)
      ? daysUntilDelinquent
      : Number.POSITIVE_INFINITY;
  }

  /**
   * Updates the payout account status.
   *
   * @param {Object} account JSON bag representing the user's account.
   * @private
   */
  _checkPayoutAccountStatus(account) {
    if (!this.currentUser.isSeller) {
      return;
    }

    const {
      payout_account_status,
      payout_account_requires_attention,
      hyperwallet_status,
    } = account;

    const status = payout_account_status != null
      ? Number.parseInt(payout_account_status)
      : null;

    setProperties(this, {
      payoutAccountStatus: status,
      payoutAccountRequiresAttention: payout_account_requires_attention,
      hyperwalletStatus: hyperwallet_status,
    });
  }

  /**
   * This will pause or unpause the heartbeat and is completely independent of the 'freezing' logic.
   *
   * This is consistent across tabs with local storage/cookies.
   *
   * @param {Boolean} isPause       Set to true if you want to pause the heartbeat, or false to start the heartbeat
   * @return {Ember.RSVP.Promise}   The current heartbeat promise.  We'll only pause AFTER the current heartbeat
   *                                has finished.
   */
  pauseHeartbeat(isPause) {
    set(this, 'heartbeatPaused', isPause);
    return Promise.resolve();
  }

  /////////////////////////////////
  //  updateHeartbeat()
  //
  // * Fetches a heartbeat from the server and updates the corresponding
  // * state properties
  /////////////////////////////////

  updateHeartbeat(heartbeat) {
    const {
      global,
      messages,
      web,
      account,
      admin: adminBag,
      activityFeed: activityFeedBag,
    } = heartbeat;

    const props = {
      adminBag,
      pollingInterval: global.interval,
      version: global.version,
      accountLocked: account.account_locked,
      daysUntilDelinquent: account.days_until_delinquent,
      needsLegal: account.needs_legal,
      threadsUnreadInbox: messages.unread_inbox_count,
      threadsUnreadArchive: messages.unread_archive_count,
      inboxHash: messages.inbox_hash,
      archiveHash: messages.archive_hash,
      activityFeedHash: activityFeedBag.activity_feed_hash,
      minWebVersion: web.minimum_version,
    };

    setProperties(this, props);

    this._checkPayoutAccountStatus(account);
  }

  getCurrentPollingInterval(errorCount) {
    if (errorCount < 5) {
      return this.pollingInterval; // no delay if there are under 5 consecutive errors
    }

    if (errorCount < 10) {
      return getMsFromMinutes(1);
    }

    if (errorCount < 15) {
      return getMsFromMinutes(5);
    }

    if (errorCount < 20) {
      return getMsFromMinutes(30);
    }

    return Number.POSITIVE_INFINITY; // just stop sending requests
  }

  start() {
    this.startPolling.perform();
    this.tosMonitor.perform();
    this.delinquentMonitor.perform();
    this.threadsUnreadInboxMonitor.perform();
    this.recentActivityMonitor.perform();
  }

  stop() {
    this.startPolling.cancelAll();
    this.tosMonitor.cancelAll();
    this.delinquentMonitor.cancelAll();
    this.threadsUnreadInboxMonitor.cancelAll();
    this.recentActivityMonitor.cancelAll();
  }

  @dropTask
  *startPolling() {
    let errorCount = 0;

    while (true) {
      yield waitForProperty(this, 'heartbeatPaused', false);

      try {
        const response = yield this.api.get(`heartbeats/${this.session.currentUser.id}`)
        this.store.pushPayload(response);

        this.updateHeartbeat(this.store.peekRecord('heartbeat', response.data.id));
        errorCount = 0;
      } catch (e) {
        // log the user out if the auth token has been invalidated
        if (isUnauthorizedError(e)) {
          this.session.invalidate();
          return;
        // user has probably masqueraded as another user, reload the page
        } else if (isForbiddenError(e)) {
          window.location.assign('/start');
          return;
        } else if (isServiceUnavailableError(e)) {
          this.router.transitionTo('maintenance');
          return;
        } else {
          errorCount += 1;
        }
      }

      const pollingInterval = this.getCurrentPollingInterval(errorCount);

      // stop polling if we have reached the maximum number of errors
      if (!Number.isFinite(pollingInterval)) {
        showModal(this, 'alert', {
          titleText: "We've lost contact",
          bodyText: 'Hmm, this window seems to have lost contact with Glimmer servers. Please refresh the page.',
          actionButtonText: 'REFRESH',
          confirmAction() {
            window.location.reload();
          },
        });
        return;
      }

      yield timeout(pollingInterval);
    }
  }

  @dropTask
  *delinquentMonitor() {
    let accountLocked;
    let daysUntilDelinquentOrInfinity = Number.POSITIVE_INFINITY;
    let modalLastShown = 0;
    let outstandingInvoices;

    while (true) {
      ({accountLocked, daysUntilDelinquentOrInfinity} = yield waitForProperty(
        this,
        'delinquencyValues',
        (value) => {
          return value.accountLocked !== accountLocked
            || value.daysUntilDelinquentOrInfinity !== daysUntilDelinquentOrInfinity;
        }
      ));

      if (accountLocked) {
        this.router.replaceWith('delinquent');
        continue;
      } else if (this.router.currentRouteName === 'delinquent') {
        this.router.replaceWith('dashboard');
        continue;
      }

      if (this.needsLegal) {
        yield waitForProperty(this, 'needsLegal', false);
        daysUntilDelinquentOrInfinity = Number.POSITIVE_INFINITY;
        continue;
      }

      const showDelinquentModal = (
          daysUntilDelinquentOrInfinity === 0
          || (daysUntilDelinquentOrInfinity < 5 && this.session.currentUser.isCompanyAdmin)
        )
        && (modalLastShown < Date.now() - 14400000) // Show once every 4 hours
      ;

      if (showDelinquentModal) {
        modalLastShown = Date.now();

        outstandingInvoices = yield this.companyCommandService.loadOutstandingInvoices(false);

        yield new Promise((resolve) => {
          showModal(this, 'delinquent-account-modal', {
            daysUntilDelinquent: daysUntilDelinquentOrInfinity,
            outstandingInvoices: outstandingInvoices,
            onClose: () => resolve(),
          });
        });
      }
    }
  }

  @dropTask
  *tosMonitor() {
    while (true) {
      yield waitForProperty(this, 'needsLegal');

      yield new Promise((resolve) => {
        const { currentUser } = this.session;

        const titleText = 'Our Terms of Service have changed';
        const bodyText = getTOSText(currentUser);

        const confirmAction = async () => {
          try {
            await this.userCommandService.acceptLatestLegal(currentUser.id);
            set(this, 'needsLegal', false);
          } finally {
            resolve();
          }
        };

        showModal(this, 'confirmation', {
          titleText,
          bodyText,
          confirmAction,
          isShowCancelButton: false,
          isDismissible: false,
        });
      });
    }
  }

  @dropTask
  *threadsUnreadInboxMonitor() {
    let previousHash = this.inboxHash;

    const getMostRecentInboxThreads = () => {
      const { id: user_id } = this.currentUser;

      return this.store.query('thread', {
        page: 1,
        count: 10,
        include: 'users',
        filter: {
          user_id,
          archived: {
            user_id,
            value: 0,
          },
        },
      });
    };

    if (!previousHash) {
      const [threads, hash] = yield all([
        getMostRecentInboxThreads(),
        waitForProperty(this, 'inboxHash'),
      ]);

      set(this, 'mostRecentInboxThreads', threads);
      previousHash = hash;
    }

    while (true) {
      const currentHash = yield waitForProperty(
        this,
        'inboxHash',
        (value) => value !== previousHash,
      );

      set(
        this,
        'mostRecentInboxThreads',
        yield getMostRecentInboxThreads(),
      );

      previousHash = currentHash;
    }
  }

  @dropTask
  *recentActivityMonitor() {
    let previousHash = this.activityFeedHash;

    const updateActivityFeed = () => this
      .activityFeedStateHybrid
      ._updateFeed
      .perform();

    if (!previousHash) {
      const [, hash] = yield all([
        updateActivityFeed(),
        waitForProperty(this, 'activityFeedHash'),
      ]);

      previousHash = hash;
    }

    while (true) {
      const currentHash = yield waitForProperty(
        this,
        'activityFeedHash',
        (value) => value !== previousHash,
      );

      yield updateActivityFeed();
      previousHash = currentHash;
    }
  }
}

export default HeartbeatStateManagerHybrid;
